import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MailOpen, Archive, Trash2, Reply, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config/api';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [expanded, setExpanded] = useState({}); // message_id -> bool
  const [replyText, setReplyText] = useState({}); // message_id -> string
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (unreadOnly) params.set('unread_only', 'true');
      const res = await fetch(`${API}/admin/messages?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unreadOnly]);

  const update = async (m, body, action) => {
    setBusyKey(`${m.message_id}-${action}`);
    try {
      const res = await fetch(`${API}/admin/messages/${m.message_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(`${action} done`);
        load();
      } else {
        toast.error('Failed');
      }
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete message "${m.subject}"?`)) return;
    setBusyKey(`${m.message_id}-delete`);
    try {
      const res = await fetch(`${API}/admin/messages/${m.message_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Deleted');
        load();
      }
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card className="p-5 glass-card" data-testid="admin-messages-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Messages</h2>
          <Badge variant="outline" className="text-xs">{total}</Badge>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={unreadOnly ? 'default' : 'outline'}
            onClick={() => setUnreadOnly((v) => !v)}
            data-testid="messages-unread-toggle"
          >
            {unreadOnly ? <MailOpen className="h-3.5 w-3.5 mr-1.5" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            {unreadOnly ? 'Unread only' : 'All'}
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>}

      {!loading && messages.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {unreadOnly ? 'No unread messages.' : 'No messages yet.'}
        </p>
      )}

      <ul className="divide-y divide-border" data-testid="admin-messages-list">
        {messages.map((m) => {
          const isOpen = !!expanded[m.message_id];
          return (
            <li
              key={m.message_id}
              className="py-3"
              data-testid={`admin-message-${m.message_id}`}
            >
              <button
                type="button"
                onClick={() => {
                  setExpanded((prev) => ({ ...prev, [m.message_id]: !prev[m.message_id] }));
                  if (!m.is_read && !isOpen) update(m, { is_read: true }, 'mark-read');
                }}
                className="w-full text-left flex items-start justify-between gap-3 hover:opacity-80"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {!m.is_read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />}
                    <p className={`text-sm truncate ${!m.is_read ? 'font-bold' : 'font-medium'}`}>{m.subject}</p>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    From {m.user_name || m.user_email} · {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
                {m.is_archived && <Badge variant="outline" className="text-[10px]">Archived</Badge>}
              </button>

              {isOpen && (
                <div className="mt-3 space-y-3 pl-4 border-l-2 border-primary/30">
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  {m.admin_reply && (
                    <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-300 mb-1">Your reply</p>
                      <p className="whitespace-pre-wrap">{m.admin_reply}</p>
                    </div>
                  )}
                  <Textarea
                    rows={3}
                    value={replyText[m.message_id] ?? (m.admin_reply || '')}
                    onChange={(e) => setReplyText((p) => ({ ...p, [m.message_id]: e.target.value }))}
                    placeholder="Type a reply to save it on the message (does not auto-send email)…"
                    data-testid={`reply-${m.message_id}`}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => update(m, { admin_reply: replyText[m.message_id] ?? '' }, 'save-reply')}
                      disabled={busyKey === `${m.message_id}-save-reply`}
                      data-testid={`save-reply-${m.message_id}`}
                    >
                      {busyKey === `${m.message_id}-save-reply` ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Reply className="h-3.5 w-3.5 mr-1.5" />}
                      Save reply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => update(m, { is_archived: !m.is_archived }, 'archive')}
                      data-testid={`archive-${m.message_id}`}
                    >
                      <Archive className="h-3.5 w-3.5 mr-1.5" /> {m.is_archived ? 'Unarchive' : 'Archive'}
                    </Button>
                    <a
                      href={`mailto:${m.contact_email}?subject=${encodeURIComponent('Re: ' + m.subject)}`}
                      className="inline-flex items-center text-xs text-primary hover:underline ml-auto"
                    >
                      <Mail className="h-3 w-3 mr-1" /> Reply via email
                    </a>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => remove(m)}
                      disabled={busyKey === `${m.message_id}-delete`}
                      data-testid={`delete-msg-${m.message_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
